/*

? Message reactions on the website (18 Oct 2026), the app's way (lib/chat/reactions.ts): one per
? person, from six emojis.
?   - ReactionPill sits under a bubble on its own side: up to three of its emojis and how many
?     people reacted, tinted when one is the viewer's. Clicking it opens the picker.
?   - ReactionPicker is the row of the six; the viewer's current one is tinted, and picking it
?     again takes it away. It sits in the hover popover and at the top of the delete dialog (a
?     long press on a touch screen), as in the app.

*/

import React from "react";
import { styled } from "@mui/system";
import { REACTION_EMOJIS, type ReactionSummary } from "../../../../lib/chat/reactionEmojis";

/* The app's tokens (bbm-android ui/theme/Color.kt ChatReaction*): the received bubble's off-white,
   the card hairline, and the brand orange washed for the viewer's own. */
const PILL_BG = "#FFFDFA";
const PILL_BORDER = "rgba(150, 110, 60, 0.14)";
const MINE_BG = "#F5EBE2";
const MINE_BORDER = "#D8AC88";
const COUNT_INK = "#5C4830";

/** How many distinct emojis the pill shows before it only counts. */
const PILL_MAX_EMOJIS = 3;

const Pill = styled("button")<{ mine: boolean }>(({ mine }) => ({
	display: "inline-flex",
	alignItems: "center",
	gap: "0.125rem",
	padding: "0.125rem 0.5rem",
	borderRadius: "999px",
	border: `1px solid ${mine ? MINE_BORDER : PILL_BORDER}`,
	background: mine ? MINE_BG : PILL_BG,
	cursor: "pointer",
	fontSize: "0.8125rem",
	lineHeight: "1rem",
	transition: "transform 100ms ease-out",
	"&:active": { transform: "scale(0.92)" },
}));

const Count = styled("span")({
	marginLeft: "0.1875rem",
	fontSize: "0.75rem",
	fontWeight: 600,
	color: COUNT_INK,
});

const PickerRow = styled("div")({
	display: "flex",
	justifyContent: "space-between",
	gap: "0.375rem",
});

const PickerButton = styled("button")<{ picked: boolean }>(({ picked }) => ({
	width: "2.5rem",
	height: "2.5rem",
	borderRadius: "50%",
	border: `1px solid ${picked ? MINE_BORDER : PILL_BORDER}`,
	background: picked ? MINE_BG : PILL_BG,
	cursor: "pointer",
	fontSize: "1.25rem",
	lineHeight: 1,
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	transition: "transform 100ms ease-out",
	"&:active": { transform: "scale(0.92)" },
}));

/** The reactions under one bubble; nothing for none. */
export const ReactionPill = ({
	reactions,
	onClick,
}: {
	reactions: Array<ReactionSummary> | undefined;
	onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
	const list = reactions || [];
	if (list.length === 0) return null;
	const total = list.reduce((sum, line) => sum + line.count, 0);
	const mine = list.some((line) => line.by_viewer);
	return (
		<Pill
			type="button"
			mine={mine}
			onClick={onClick}
			aria-label={list.map((line) => `${line.emoji} ${line.count}`).join(", ") + (mine ? ", including yours" : "")}
		>
			{list.slice(0, PILL_MAX_EMOJIS).map((line) => (
				<span key={line.emoji}>{line.emoji}</span>
			))}
			{/* One reaction needs no number; WhatsApp counts from two. */}
			{total > 1 ? <Count>{total}</Count> : null}
		</Pill>
	);
};

/** The six emojis, the viewer's [current] one tinted. */
export const ReactionPicker = ({ current, onPick }: { current: string | undefined; onPick: (emoji: string) => void }) => (
	<PickerRow>
		{REACTION_EMOJIS.map((emoji) => (
			<PickerButton
				key={emoji}
				type="button"
				picked={emoji === current}
				onClick={() => onPick(emoji)}
				aria-label={emoji === current ? `Remove ${emoji} reaction` : `React with ${emoji}`}
			>
				{emoji}
			</PickerButton>
		))}
	</PickerRow>
);
